
import { LockScreenService } from "@/services/lockscreen-service";
import AppErrorMessageBox from "@/components/common/ErrorMessageBox.vue";
import Vue from "vue";
import { TranslateResult } from "vue-i18n";
import { AuthService, AuthSocketService } from "@/services";
import { BrowserInfo } from "@/utils/browser";
import { ElectronFunctions } from "@/utils/electron/electron";
import {ScannerListener} from "@/utils/scanner";
import { UnifiedNfcReader } from "@/utils/unified-nfc-reader";
import { HttpError } from "@/utils/services/http-error";
import AppNfcPinCodeInput from "@/components/account/NfcPinCodeInput.vue";

export default Vue.extend({
	data(){
		return {
			LockScreenService,
			password: "",
			isSending: false,
			error: "&#200B;" as TranslateResult,
			errorTimer: 0,
			nfcSettings: ElectronFunctions.getNfcSettings(),
			hasNfcTags: false,
			queuedFetchBadges: false,
			enteredTag: "",
			pinCodeRequired: false,
			pinCode: ""
		};
	},
	computed: {
		isLocked():boolean{
			return this.LockScreenService.isLocked;
		},
		emptyErrorMessage():TranslateResult{
			return "&#200B;";
		},
		userName():string{
			return AuthService.wfUser.getShortDisplayName();
		},
		instructionMessage():string{
			if (BrowserInfo.isElectron()){
				if(!this.passwordFieldVisible) {
					return "scan-nfc-badge-to-unlock";
				}
				return "insert-password-or-scan-nfc-badge-to-unlock";
			}
			return "insert-password-to-unlock";
		},
		passwordFieldVisible():boolean {
			if (!this.hasNfcTags) return true;
			if (!this.nfcSettings.requireTagOnStart) return true;
			return false;
		}
	},
	watch: {
		"isLocked": {
			immediate: true,
			async handler(){
				this.hasNfcTags = true;
				try{
					await this.fetchBadgesCount();
				}catch(err){
					this.hasNfcTags = false;
				}
				if (this.isLocked){
					ScannerListener.onInput(this.onNfcChipScanned);
				}else{
					ScannerListener.offInput(this.onNfcChipScanned);
				}
			}
		}
	},
	methods: {
		async submit(){
			if (this.isSending) return;
			this.isSending = true;
			clearTimeout(this.errorTimer);
			try{
				let result = await AuthService.confirmPassword(this.password);
				if (result) {
					return this.unlock();
				}
				this.error = this.$ct("password-incorrect");
				this.errorTimer = setTimeout(()=>{
					this.error = "&#200B;";
				}, 5000);
			}catch(err){

			}finally{
				this.isSending = false;
			}
		},
		async submitPinCode(){
			if (this.isSending) return;
			try{
				this.isSending = true;
				this.onNfcChipScanned(this.enteredTag);
			}catch(err){

			}finally{
				this.isSending = false;
			}
		},
		emptyInputs(){
			this.password = "";
			this.enteredTag = "";
			this.pinCode = "";
			this.error = this.emptyErrorMessage;
			this.pinCodeRequired = false;
		},
		unlock(){
			LockScreenService.isLocked = false;
			this.emptyInputs();
		},
		lock(){
			this.nfcSettings = ElectronFunctions.getNfcSettings();
			UnifiedNfcReader.onLock();
			LockScreenService.isLocked = true;
		},
		onKeyDown(event:KeyboardEvent){
			if (!event.ctrlKey) return;
			if (event.code != "KeyL") return;
			event.preventDefault();
			event.stopPropagation();
			this.lock();
		},
		async onNfcChipScanned(uid:string){
			try{
				if (!LockScreenService.isLocked) {
					let settings = ElectronFunctions.getNfcSettings();
					if (settings.scanToLock){
						this.lock();
					}
					if (LockScreenService.currentNfcTag != uid) {
						let result = await LockScreenService.validateNfcBadge(uid, "");
						if (!result.Valid) return;
						LockScreenService.currentNfcTag = uid;
						return;
					}
					if (!settings.requireTagOnStart) {
						return;
					}
					let result = await LockScreenService.validateNfcBadge(uid, "");
					if (!result.Valid) return;
					this.lock();
					return;
				}
				let result = await LockScreenService.validateNfcBadge(uid, this.pinCode);
				if (!result.Valid) return;
				this.unlock();
				LockScreenService.currentNfcTag = uid;
			}catch(err){
				console.log("In catch");
				let hErr = err as HttpError;
				if (hErr.key == "PIN_CODE_REQUIRED") {
					hErr.dontShow = true;
					this.lock();
					this.enteredTag = uid;
					this.pinCodeRequired = true;
				}else if(hErr.key == "PIN_CODE_INCORRECT"){
					hErr.dontShow = true;
					this.error = this.$ct("pin-code-incorrect");
					this.pinCode = "";
				}
			}
		},
		async fetchBadgesCount(){
			this.queuedFetchBadges = false;
			let badges = await LockScreenService.getNfcBadges();
			this.hasNfcTags = badges.length > 0;
		},
		queueFetchBadges(){
			if (document.hasFocus()) {
				this.fetchBadgesCount();
			}else{
				this.queuedFetchBadges = true;
			}
		},
		fetchBadgesIfQueued(){
			if (!this.queuedFetchBadges) return;
			this.fetchBadgesCount();
		},
		cancelPinCode(){
			this.emptyInputs();
		}
	},
	created(){
		console.log("LOCKSCREEN CREATED");
		UnifiedNfcReader.init(this);
		UnifiedNfcReader.on(UnifiedNfcReader.READER_SCANNED_EVENT, this.onNfcChipScanned);
		window.addEventListener("keydown", this.onKeyDown);
		AuthSocketService.on("/lock-screen-nfc-badges/badge-added", this.queueFetchBadges);
		AuthSocketService.on("/lock-screen-nfc-badges/badge-removed", this.queueFetchBadges);
		window.addEventListener("focus", this.fetchBadgesIfQueued);
	},
	destroyed(){
		window.removeEventListener("keydown", this.onKeyDown);
		UnifiedNfcReader.off(UnifiedNfcReader.READER_SCANNED_EVENT, this.onNfcChipScanned);
		ScannerListener.offInput(this.onNfcChipScanned);
		AuthSocketService.off("/lock-screen-nfc-badges/badge-added", this.queueFetchBadges);
		AuthSocketService.off("/lock-screen-nfc-badges/badge-removed", this.queueFetchBadges);
		window.removeEventListener("focus", this.fetchBadgesIfQueued);
	},
	components: {
		AppErrorMessageBox,
		AppNfcPinCodeInput
	},
	i18nEx: {
		componentPrefix: "components.LockScreen"
	}
});
